import React from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useT } from "@/lib/LanguageContext";
import { tenantHref } from "@/lib/tenantPath";
// Relative on purpose: pure modules and components, resolvable under Jest without a shim.
import LoadFailure from "../../components/ui/LoadFailure";
import useMemberPathway from "../../pathways/useMemberPathway";
import { stepPath } from "../../pathways/learnerModel";
import { PATHWAYS_LEARNER as IDS } from "../../constants/testIds/pathwaysLearner";
import PathwayHomeView from "./PathwayHomeView";
import PathwayNotFound from "./PathwayNotFound";

/**
 * The pathway home's container: reads `GET /client/pathways/{id}` and renders
 * PathwayHomeView over it. A step opens its own screen, `/parcours/:id/etape/:stepId`.
 *
 *   loading        spinner
 *   404            "not found" (absent, draft or not joined: one answer, contract §5.4)
 *   other failure  load failure with retry
 *
 * Mount only under a signed-in client, keyed by the pathway id.
 *
 * @param {{ pathwayId: string }} props
 */
export default function PathwayHome({ pathwayId }) {
  const t = useT("public");
  const navigate = useNavigate();
  const { data, loading, error, notFound, reload } = useMemberPathway(pathwayId);

  if (loading && !data) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="animate-spin text-eh-sage-deep" size={28} aria-hidden="true" />
      </div>
    );
  }
  if (notFound) return <PathwayNotFound />;
  if (error || !data) {
    return (
      <LoadFailure
        message={t("pathways.loadFailed")}
        onRetry={reload}
        retryLabel={t("pathways.retry")}
        testId={IDS.loadError}
      />
    );
  }
  return (
    <PathwayHomeView
      home={data}
      onOpenStep={(stepId) => navigate(tenantHref(stepPath(pathwayId, stepId)))}
    />
  );
}
